import React, { useState, useMemo } from 'react';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  Key,
  Database,
  Settings,
  Code,
  Search,
  ChevronDown,
  ChevronUp,
  Bot,
  Zap,
  Box,
  Copy,
  Check,
} from 'lucide-react';
import { SecurityAnalysis as SecurityAnalysisType, SecurityVulnerability } from '../types';
import HeuristicNotice from './HeuristicNotice';

interface SecurityAnalysisProps {
  security: SecurityAnalysisType;
}

const SEVERITY_STYLES: Record<string, string> = {
  critical: 'bg-sev-critical/10 text-sev-critical border border-sev-critical/40',
  high:     'bg-sev-high/10 text-sev-high border border-sev-high/40',
  medium:   'bg-sev-medium/10 text-sev-medium border border-sev-medium/40',
  low:      'bg-sev-low/10 text-sev-low border border-sev-low/40',
  info:     'bg-gray-100 text-gray-700 border border-gray-200',
};

const SEVERITY_LEFT_BORDER: Record<string, string> = {
  critical: 'border-l-4 border-l-red-500',
  high:     'border-l-4 border-l-orange-500',
  medium:   'border-l-4 border-l-yellow-500',
  low:      'border-l-4 border-l-blue-400',
  info:     'border-l-4 border-l-gray-300',
};

const SEVERITY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

function CategoryIcon({ category }: { category: string }) {
  const cls = 'w-3.5 h-3.5';
  switch (category) {
    case 'dependency':    return <Database className={cls} />;
    case 'secrets':       return <Key className={cls} />;
    case 'configuration': return <Settings className={cls} />;
    case 'code':          return <Code className={cls} />;
    case 'vibeCode':      return <Bot className={cls} />;
    case 'iac':           return <Box className={cls} />;
    default:              return <AlertTriangle className={cls} />;
  }
}

function AvsvBadge({ level }: { level: 1 | 2 | 3 | null }) {
  if (level === null) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sev-critical/10 border border-sev-critical/30 text-sm font-semibold text-sev-critical">
        <AlertTriangle className="w-4 h-4" />
        ASVS: Needs Remediation
      </div>
    );
  }
  const colors = ['', 'bg-sev-medium/10 border-sev-medium/40 text-sev-medium', 'bg-blue-50 border-blue-300 text-blue-800', 'bg-limedim border-lime/40 text-lime'];
  const labels = ['', 'Level 1 Ready', 'Level 2 Ready', 'Level 3 Ready'];
  const descs = ['', 'Basic security (low-risk app)', 'Standard — suitable for most business apps', 'Advanced'];
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-semibold ${colors[level]}`}>
      <Shield className="w-4 h-4" />
      OWASP ASVS {labels[level]}
      <span className="font-normal text-xs opacity-75">· {descs[level]}</span>
    </div>
  );
}

function FindingCard({ vuln }: { vuln: SecurityVulnerability }) {
  const [expanded, setExpanded] = useState(false);
  const [fixCopied, setFixCopied] = useState(false);

  const copyFix = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!vuln.fixExample) return;
    navigator.clipboard.writeText(vuln.fixExample);
    setFixCopied(true);
    setTimeout(() => setFixCopied(false), 2000);
  };

  return (
    <div className={`bg-charcoal-card rounded-lg border ${SEVERITY_LEFT_BORDER[vuln.severity]} shadow-sm`}>
      <button
        className="w-full text-left p-4"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase ${SEVERITY_STYLES[vuln.severity]}`}>
                {vuln.severity}
              </span>
              <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                <CategoryIcon category={vuln.category} />
                {vuln.category}
              </span>
              {vuln.owaspCategory && (
                <span className="text-xs text-accent-teal bg-accent-teal/10 border border-accent-teal/30 px-2 py-0.5 rounded-full">
                  {vuln.owaspCategory}
                </span>
              )}
              {vuln.cve && (
                <span className="text-xs font-mono text-gray-500">{vuln.cve}</span>
              )}
            </div>
            <h4 className="font-semibold text-gray-900">{vuln.title}</h4>
            <p className="text-sm text-gray-600 mt-0.5">{vuln.description}</p>
            {vuln.affectedFiles.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {vuln.affectedFiles.slice(0, 3).map((f, i) => (
                  <span key={i} className="text-xs font-mono bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                    {f}{vuln.lineNumber ? `:${vuln.lineNumber}` : ''}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="text-gray-400 flex-shrink-0 pt-0.5">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
          {/* Vulnerable code snippet */}
          {vuln.vulnerableCode && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Vulnerable Code</p>
              <pre className="bg-sev-critical/10 border border-sev-critical/30 text-sev-critical text-xs p-3 rounded overflow-x-auto font-mono">
                {vuln.vulnerableCode}
              </pre>
            </div>
          )}

          {/* Fix example */}
          {vuln.fixExample && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-gray-500 uppercase">How to Fix</p>
                <button
                  onClick={copyFix}
                  className="flex items-center gap-1 text-xs text-lime hover:text-lime bg-limedim hover:bg-lime/20 px-2 py-0.5 rounded transition-colors"
                >
                  {fixCopied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {fixCopied ? 'Copied!' : 'Copy Fix'}
                </button>
              </div>
              <pre className="bg-limedim border border-lime/30 text-lime text-xs p-3 rounded overflow-x-auto font-mono whitespace-pre-wrap">
                {vuln.fixExample}
              </pre>
            </div>
          )}

          {/* Recommendation (plain English) */}
          {!vuln.fixExample && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
              <span className="font-semibold">Fix: </span>{vuln.recommendation}
            </div>
          )}

          {vuln.cvss !== undefined && (
            <p className="text-xs text-gray-500">CVSS Score: {vuln.cvss}</p>
          )}
        </div>
      )}
    </div>
  );
}

const SecurityAnalysis: React.FC<SecurityAnalysisProps> = ({ security }) => {
  const [severityFilter, setSeverityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAll, setShowAll] = useState(false);

  const sortedVulns = useMemo(() =>
    [...security.vulnerabilities].sort((a, b) =>
      (SEVERITY_ORDER[a.severity] ?? 5) - (SEVERITY_ORDER[b.severity] ?? 5)
    ), [security.vulnerabilities]);

  const topFindings = useMemo(() =>
    sortedVulns.filter(v => v.severity === 'critical' || v.severity === 'high').slice(0, 5),
    [sortedVulns]);

  const filteredVulns = useMemo(() => {
    let list = sortedVulns;
    if (severityFilter !== 'all') list = list.filter(v => v.severity === severityFilter);
    if (categoryFilter !== 'all') list = list.filter(v => v.category === categoryFilter);
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      list = list.filter(v =>
        v.title.toLowerCase().includes(t) ||
        v.description.toLowerCase().includes(t) ||
        v.affectedFiles.some(f => f.toLowerCase().includes(t)) ||
        v.owaspCategory?.toLowerCase().includes(t)
      );
    }
    return list;
  }, [sortedVulns, severityFilter, categoryFilter, searchTerm]);

  const stats = useMemo(() => {
    const s = { critical: 0, high: 0, medium: 0, low: 0 };
    security.vulnerabilities.forEach(v => { if (v.severity in s) (s as any)[v.severity]++; });
    return s;
  }, [security.vulnerabilities]);

  const displayedVulns = showAll ? filteredVulns : filteredVulns.slice(0, 10);

  return (
    <div className="space-y-6">

      {/* ASVS Level Banner */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
        <div>
          <p className="text-sm text-gray-500 mb-1">OWASP Application Security Verification Standard</p>
          <AvsvBadge level={security.avsvLevel} />
          <HeuristicNotice context="asvs" />
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-400">Security Score</p>
          <p className={`text-3xl font-bold ${security.overallScore >= 80 ? 'text-lime' : security.overallScore >= 60 ? 'text-sev-medium' : 'text-sev-critical'}`}>
            {security.overallScore}%
          </p>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            security.riskLevel === 'critical' ? 'bg-sev-critical/10 text-sev-critical' :
            security.riskLevel === 'high' ? 'bg-sev-high/10 text-sev-high' :
            security.riskLevel === 'medium' ? 'bg-sev-medium/10 text-sev-medium' :
            'bg-limedim text-lime'
          }`}>
            {security.riskLevel.toUpperCase()} RISK
          </span>
        </div>
      </div>

      {/* Vibe-Code / AI-Generated Code Risk Panel */}
      {security.vibeCodeAnalysis && (security.vibeCodeAnalysis.isVibeCodeProject || security.vibeCodeAnalysis.totalVibeCodeFindings > 0) && (
        <div className={`rounded-xl border-2 p-5 ${
          security.vibeCodeAnalysis.vibeCodeRiskScore >= 50 ? 'border-amber-400 bg-accent-amber/10' :
          security.vibeCodeAnalysis.vibeCodeRiskScore >= 20 ? 'border-sev-medium/40 bg-sev-medium/10' :
          'border-blue-300 bg-blue-50'
        }`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-amber-600" />
              <span className="font-bold text-gray-900">AI-Generated Code Risk Scanner</span>
              <span className="text-xs bg-accent-amber/10 text-accent-amber border border-accent-amber/40 px-2 py-0.5 rounded-full font-medium">
                {security.vibeCodeAnalysis.isVibeCodeProject ? 'AI/Vibe-coded project detected' : 'AI patterns found'}
              </span>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">AI Risk Score</p>
              <p className={`text-2xl font-bold ${
                security.vibeCodeAnalysis.vibeCodeRiskScore >= 50 ? 'text-sev-critical' :
                security.vibeCodeAnalysis.vibeCodeRiskScore >= 20 ? 'text-amber-600' :
                'text-lime'
              }`}>{security.vibeCodeAnalysis.vibeCodeRiskScore}/100</p>
            </div>
          </div>

          {/* AI Libraries detected */}
          {security.vibeCodeAnalysis.aiLibrariesDetected.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              <span className="text-xs text-gray-600 mr-1">AI libs detected:</span>
              {security.vibeCodeAnalysis.aiLibrariesDetected.map(lib => (
                <span key={lib} className="text-xs bg-charcoal-card border border-accent-amber/40 text-accent-amber px-2 py-0.5 rounded-full font-mono">{lib}</span>
              ))}
            </div>
          )}

          {/* Risk breakdown */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Prompt Injection', count: security.vibeCodeAnalysis.promptInjectionRisks, icon: '💉', color: 'red' },
              { label: 'Missing Auth', count: security.vibeCodeAnalysis.missingAuthChecks, icon: '🔓', color: 'orange' },
              { label: 'Exposed Endpoints', count: security.vibeCodeAnalysis.exposedAIEndpoints, icon: '🌐', color: 'yellow' },
              { label: 'Hardcoded AI Keys', count: security.vibeCodeAnalysis.hardcodedAIKeys, icon: '🔑', color: 'red' },
            ].map(item => (
              <div key={item.label} className="bg-charcoal-card rounded-lg p-3 border border-gray-200 text-center">
                <div className="text-lg">{item.icon}</div>
                <div className={`text-xl font-bold ${item.count > 0 ? 'text-sev-critical' : 'text-lime'}`}>{item.count}</div>
                <div className="text-xs text-gray-600">{item.label}</div>
              </div>
            ))}
          </div>

          {security.vibeCodeAnalysis.totalVibeCodeFindings === 0 && (
            <div className="mt-3 flex items-center gap-2 text-sm text-lime">
              <CheckCircle className="w-4 h-4" />
              No AI-specific security risks detected — good vibe-coding hygiene!
            </div>
          )}
          {security.vibeCodeAnalysis.totalVibeCodeFindings > 0 && (
            <div className="mt-3 flex items-center gap-2 text-sm text-accent-amber">
              <Zap className="w-4 h-4" />
              <span><strong>{security.vibeCodeAnalysis.totalVibeCodeFindings} AI-specific risk{security.vibeCodeAnalysis.totalVibeCodeFindings > 1 ? 's' : ''}</strong> found — see findings below filtered by "AI Code" category</span>
            </div>
          )}
        </div>
      )}

      {/* Severity Summary */}
      <div className="grid grid-cols-4 gap-3">
        {(['critical', 'high', 'medium', 'low'] as const).map(s => (
          <div key={s} className={`text-center p-3 rounded-lg ${
            s === 'critical' ? 'bg-sev-critical/10' : s === 'high' ? 'bg-sev-high/10' : s === 'medium' ? 'bg-sev-medium/10' : 'bg-blue-50'
          }`}>
            <div className={`text-2xl font-bold ${
              s === 'critical' ? 'text-sev-critical' : s === 'high' ? 'text-sev-high' : s === 'medium' ? 'text-sev-medium' : 'text-blue-600'
            }`}>{stats[s]}</div>
            <div className="text-xs capitalize text-gray-600">{s}</div>
          </div>
        ))}
      </div>

      {/* Category Scores */}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 bg-charcoal-card border rounded-lg">
          <p className="text-xs text-gray-500 mb-0.5">Dependency Security</p>
          <p className={`text-lg font-bold ${security.dependencySecurity.dependencyScore >= 80 ? 'text-lime' : security.dependencySecurity.dependencyScore >= 60 ? 'text-sev-medium' : 'text-sev-critical'}`}>
            {security.dependencySecurity.dependencyScore}%
          </p>
          <p className="text-xs text-gray-400">{security.dependencySecurity.vulnerableDependencies} vulnerable deps</p>
        </div>
        <div className="p-3 bg-charcoal-card border rounded-lg">
          <p className="text-xs text-gray-500 mb-0.5">Code Security</p>
          <p className={`text-lg font-bold ${(security.codeSecurity?.codeScore || 0) >= 80 ? 'text-lime' : (security.codeSecurity?.codeScore || 0) >= 60 ? 'text-sev-medium' : 'text-sev-critical'}`}>
            {security.codeSecurity?.codeScore || 0}%
          </p>
          <p className="text-xs text-gray-400">{security.codeSecurity?.securityIssues?.length || 0} issues</p>
        </div>
        <div className="p-3 bg-charcoal-card border rounded-lg">
          <p className="text-xs text-gray-500 mb-0.5">Secrets</p>
          <p className={`text-lg font-bold ${security.secretsManagement.secretsScore >= 80 ? 'text-lime' : security.secretsManagement.secretsScore >= 60 ? 'text-sev-medium' : 'text-sev-critical'}`}>
            {security.secretsManagement.secretsScore}%
          </p>
          <p className="text-xs text-gray-400">{security.secretsManagement.secretsInCode} secrets in code</p>
        </div>
      </div>

      {/* Top Critical Findings (opinionated — max 5) */}
      {topFindings.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            Top Priority Issues — Fix These First
          </h3>
          <div className="space-y-2">
            {topFindings.map(v => <FindingCard key={v.id} vuln={v} />)}
          </div>
        </div>
      )}

      {topFindings.length === 0 && security.vulnerabilities.length === 0 && (
        <div className="text-center py-10">
          <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
          <p className="font-semibold text-gray-700">No vulnerabilities detected</p>
          <p className="text-sm text-gray-500 mt-1">This repository passed all security checks.</p>
        </div>
      )}

      {/* All Findings — filterable */}
      {security.vulnerabilities.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
            All Findings ({security.vulnerabilities.length})
          </h3>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select value={severityFilter} onChange={e => setSeverityFilter(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="all">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="all">All Categories</option>
              <option value="vibeCode">🤖 AI Code Risks</option>
              <option value="iac">📦 IaC / Infrastructure</option>
              <option value="secrets">Secrets</option>
              <option value="code">Code</option>
              <option value="dependency">Dependencies</option>
              <option value="configuration">Configuration</option>
            </select>
          </div>

          <p className="text-xs text-gray-500 mb-2">Showing {displayedVulns.length} of {filteredVulns.length}</p>

          <div className="space-y-2">
            {displayedVulns.map(v => <FindingCard key={v.id} vuln={v} />)}
          </div>

          {filteredVulns.length > 10 && !showAll && (
            <button
              onClick={() => setShowAll(true)}
              className="w-full mt-3 py-2 text-sm text-blue-600 hover:text-blue-800 border border-blue-200 rounded-lg hover:bg-blue-50"
            >
              Show all {filteredVulns.length} findings
            </button>
          )}
        </div>
      )}

      {/* Compliance Summary */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Compliance Overview</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(security.compliance.complianceReports).map(([name, report]) => (
            <div key={name} className="p-3 border rounded-lg text-center">
              <div className={`w-8 h-8 rounded-full mx-auto mb-1 flex items-center justify-center ${report.score >= 80 ? 'bg-limedim' : report.score >= 60 ? 'bg-sev-medium/10' : 'bg-sev-critical/10'}`}>
                {report.score >= 80
                  ? <CheckCircle className="w-4 h-4 text-lime" />
                  : <AlertTriangle className="w-4 h-4 text-red-500" />}
              </div>
              <p className="text-xs font-semibold uppercase text-gray-700">{name}</p>
              <p className={`text-sm font-bold ${report.score >= 80 ? 'text-lime' : report.score >= 60 ? 'text-sev-medium' : 'text-sev-critical'}`}>
                {report.score}%
              </p>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default SecurityAnalysis;
